import { ScaleLinear, scaleLinear } from "d3";
import { useMemo } from "react";

import { getActionPath, getActionPathSegments, getPathLength } from "../../utils";
import { ActionLineProps } from "./types";

export interface UseActionPathProps extends ActionLineProps {
  pathGenerator?: (path: string, i: number, arr: string[]) => string;
}

const defaultPathGenerator = (path: string) => path;

export const useActionPath = ({ action, controlPoints, pathGenerator = defaultPathGenerator }: UseActionPathProps) => {
  const actionWithControlPoints = useMemo(
    () =>
      controlPoints
        ? {
            ...action,
            ...(controlPoints[0] && { pos: controlPoints[0] }),
            ...(controlPoints.slice(1, -1) && { midPoints: controlPoints.slice(1, -1) }),
            ...(controlPoints[controlPoints.length - 1] && { endPos: controlPoints[controlPoints.length - 1] }),
          }
        : action,
    [action, controlPoints]
  );

  const { pos, midPoints, endPos, startTime, endTime } = actionWithControlPoints;

  const straightPathData = useMemo(() => getActionPath({ pos, midPoints, endPos }), [pos, midPoints, endPos]);
  const pathData = useMemo(() => getActionPath({ pos, midPoints, endPos }), [pos, midPoints, endPos]);
  const paths = useMemo(() => getActionPathSegments({ pos, midPoints, endPos }), [pos, midPoints, endPos]);
  const pathLength = useMemo(() => getPathLength(pathData), [pathData]);

  const pathsWithScales = useMemo(
    () =>
      paths.reduce<
        { pathString: string; scale: ScaleLinear<number, number>; pathPercentageScale: ScaleLinear<number, number> }[]
      >((acc, path, i) => {
        if (startTime !== undefined && endTime !== undefined) {
          const segmentLength = getPathLength(path);
          const prevEndTime = acc[i - 1]?.scale?.domain()?.[1] || startTime;
          const pathPercentage = segmentLength / pathLength;

          const generatedPathSegement = pathGenerator(path, i, paths);
          const generatedPathSegementLength = getPathLength(generatedPathSegement);

          const pathLengthScale = scaleLinear()
            .domain([prevEndTime, prevEndTime + pathPercentage * (endTime - startTime)])
            .range([0, -generatedPathSegementLength])
            .clamp(true);

          const pathPercentageScale = scaleLinear()
            .domain([prevEndTime, prevEndTime + pathPercentage * (endTime - startTime)])
            .range([0, 1])
            .clamp(true);

          return [...acc, { pathString: generatedPathSegement, scale: pathLengthScale, pathPercentageScale }];
        } else
          return [
            ...acc,
            {
              pathString: pathGenerator(path, i, paths),
              scale: scaleLinear().domain([0, 0]).range([0, 0]),
              pathPercentageScale: scaleLinear().domain([0, 0]).range([0, 0]),
            },
          ];
      }, []),
    [paths, pathLength, startTime, endTime, pathGenerator]
  );

  return { straightPathData, pathData, pathsWithScales, endTime };
};
